<template>
  <div class="min-h-screen bg-gradient-to-b from-[#142a4a] via-[#18365f] to-[#1b4475]">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 class="text-3xl font-bold text-slate-100">Banco de Dados</h1>
          <p class="text-slate-400 mt-1 text-sm">
            Configuração de conectores de banco de dados
          </p>
        </div>
        <button
          type="button"
          class="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 text-sm font-semibold transition"
          @click="openConnectorModal(null)"
        >
          Nova conexão
        </button>
      </div>

      <div v-if="loadError" class="mb-4 p-4 bg-red-500/10 border border-red-400/40 text-red-300 rounded-lg text-sm">
        {{ loadError }}
      </div>

      <div v-if="loading" class="text-center py-12 text-slate-400">Carregando…</div>

      <ul v-else class="space-y-4">
        <li
          v-for="c in connectors"
          :key="c.id"
          class="rounded-xl border-2 border-cyan-400/35 bg-slate-900/85 backdrop-blur-sm p-5 hover:border-cyan-400/70 transition"
        >
          <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2">
                <h2 class="text-lg font-semibold text-slate-100">{{ c.name }}</h2>
                <span
                  class="text-xs px-2 py-0.5 rounded-full border"
                  :class="c.active ? 'border-emerald-400/40 text-emerald-300 bg-emerald-500/10' : 'border-slate-600 text-slate-400'"
                >
                  {{ c.active ? 'Ativa' : 'Inativa' }}
                </span>
                <span v-if="c.db_type" class="text-xs text-slate-400 border border-slate-600 rounded-full px-2 py-0.5">
                  {{ c.db_type }}
                </span>
              </div>
              <p class="text-sm font-mono text-slate-400 mt-1 break-all">
                {{ c.host }}{{ c.database ? ` / ${c.database}` : '' }}
              </p>
              <p class="text-xs text-slate-500 mt-1">{{ c.views_count ?? 0 }} view(s)</p>
            </div>

            <div class="flex flex-wrap gap-2">
              <button
                type="button"
                class="px-3 py-1.5 text-sm rounded-lg border border-slate-500/60 text-slate-200 hover:bg-slate-800/80"
                @click="toggleExpand(c.id)"
              >
                {{ expandedId === c.id ? 'Ocultar views' : 'Visualizar views' }}
              </button>
              <button
                type="button"
                class="px-3 py-1.5 text-sm rounded-lg border border-cyan-400/45 text-cyan-200 hover:bg-cyan-400/12"
                @click="openConnectorModal(c.id)"
              >
                Editar
              </button>
              <button
                type="button"
                class="px-3 py-1.5 text-sm rounded-lg border border-red-400/45 text-red-200 hover:bg-red-500/20"
                @click="openConnectorDeleteConfirm(c)"
              >
                Excluir
              </button>
            </div>
          </div>

          <div v-if="expandedId === c.id" class="mt-4 pt-4 border-t border-slate-700/70">
            <div v-if="detailLoading" class="text-slate-400 text-sm py-4">Carregando detalhes…</div>
            <template v-else-if="detail && detail.id === c.id">
              <div class="flex justify-between items-center mb-3 gap-3 flex-wrap">
                <h3 class="text-sm font-medium text-slate-300">Views parametrizadas</h3>
                <button
                  type="button"
                  class="text-sm px-3 py-1 rounded-lg bg-[#2AD7DE]/20 text-cyan-200 border border-cyan-400/40 hover:bg-[#2AD7DE]/30"
                  @click="openViewQueryModal(c.id, null)"
                >
                  + View
                </button>
              </div>

              <ul v-if="detail.views && detail.views.length" class="space-y-2">
                <li
                  v-for="q in detail.views"
                  :key="q.id"
                  class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-slate-600/60 bg-slate-950/40 p-3 text-sm"
                >
                  <div class="min-w-0">
                    <div class="font-medium text-slate-200">
                      <span class="font-mono text-cyan-300/90">{{ q.view_name }}</span>
                    </div>
                    <div class="text-slate-400">
                      {{ q.name }} · <span class="font-mono text-slate-300">{{ q.operation_key }}</span>
                    </div>
                    <div v-if="(q.parameter_schema || []).length" class="text-xs text-slate-500 mt-1">
                      Parâmetros:
                      <span v-for="(p, i) in q.parameter_schema" :key="i">
                        {{ p.name }} ({{ p.column }}{{ p.fixed ? ', fixo' : '' }})<span v-if="i < q.parameter_schema.length - 1">, </span>
                      </span>
                    </div>
                  </div>

                  <div class="flex gap-2">
                    <button
                      type="button"
                      class="px-2 py-1 rounded border border-slate-500 text-slate-300 hover:bg-slate-800 text-xs"
                      @click="openViewQueryModal(c.id, q.id)"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      class="px-2 py-1 rounded border border-red-400/40 text-red-300 hover:bg-red-500/15 text-xs"
                      @click="openViewQueryDeleteConfirm(c.id, q)"
                    >
                      Excluir
                    </button>
                  </div>
                </li>
              </ul>

              <p v-else class="text-slate-500 text-sm">
                Nenhuma view configurada.
              </p>
            </template>
          </div>
        </li>
      </ul>
    </div>

    <!-- Connector modal -->
    <div
      v-if="connectorModalOpen"
      class="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div class="px-6 py-4 bg-[#2f4563] border-b border-cyan-200/25 flex justify-between items-center flex-shrink-0">
          <h2 class="text-xl font-semibold text-slate-100">
            {{ editingConnectorId ? 'Editar conector de Banco de Dados' : 'Novo conector de Banco de Dados' }}
          </h2>
          <button @click="closeConnectorModal" class="text-slate-500 hover:text-cyan-300 transition">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form @submit.prevent="openConnectorSaveConfirm" class="flex flex-col flex-1 min-h-0">
          <div
            class="px-6 py-4 overflow-y-auto flex-1 min-h-0 [scrollbar-width:thin] [scrollbar-color:rgba(103,232,249,0.45)_rgba(47,69,99,0.4)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#2f4563]/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-cyan-300/45 [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-[#2f4563]/80 [&::-webkit-scrollbar-thumb:hover]:bg-cyan-300/70"
          >
            <div v-if="connectorModalLoading" class="py-16 text-center text-slate-400 text-sm">
              Carregando dados do conector…
            </div>
            <template v-else>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Nome <span class="text-red-400">*</span></label>
                <input v-model="connectorForm.name" type="text" required
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70" />
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Tipo do banco <span class="text-red-400">*</span></label>
                <select v-model="connectorForm.db_type" required
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70 text-sm">
                  <option value="postgresql" class="bg-slate-900">PostgreSQL</option>
                  <option value="mysql" class="bg-slate-900">MySQL</option>
                  <option value="sqlserver" class="bg-slate-900">SQL Server</option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Host <span class="text-red-400">*</span></label>
                <input v-model="connectorForm.host" type="text" required
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70" />
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Porta</label>
                <input v-model="connectorForm.port" type="number"
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70" />
              </div>

              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-slate-300 mb-1">Database</label>
                <input v-model="connectorForm.database" type="text"
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70" />
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Usuário</label>
                <input v-model="connectorForm.username" type="text"
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70" />
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Senha</label>
                <div class="relative">
                  <input
                    v-model="connectorForm.password"
                    :type="showConnectorPassword ? 'text' : 'password'"
                    autocomplete="new-password"
                    class="w-full pl-3 pr-11 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                  />
                  <button
                    type="button"
                    class="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-slate-400 hover:text-[#2AD7DE] focus:outline-none focus-visible:text-[#2AD7DE] rounded-r-lg focus:ring-0"
                    :aria-label="showConnectorPassword ? 'Ocultar senha' : 'Mostrar senha'"
                    :aria-pressed="showConnectorPassword"
                    @click="showConnectorPassword = !showConnectorPassword"
                  >
                    <svg
                      v-if="!showConnectorPassword"
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    <svg
                      v-else
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      />
                    </svg>
                  </button>
                </div>
                <label class="flex items-center gap-2 text-xs text-slate-300 mt-2">
                  <input type="checkbox" v-model="connectorForm.clear_password" />
                  Remover senha atual
                </label>
              </div>

              <div class="md:col-span-2">
                <label class="flex items-center gap-2 text-slate-300 text-sm">
                  <input type="checkbox" v-model="connectorForm.active" />
                  Ativa
                </label>
              </div>
            </div>

            <div v-if="connectorTestResult" class="mt-4">
              <div
                class="bg-emerald-500/10 border border-emerald-400/35 text-emerald-200 px-4 py-3 rounded-lg text-sm"
              >
                {{ connectorTestResult.message }}
              </div>
            </div>
            </template>
          </div>

          <div class="px-6 py-4 border-t border-slate-600/70 flex flex-col gap-3 flex-shrink-0 bg-slate-700/55">
            <div
              v-if="connectorModalError && !connectorModalLoading"
              role="alert"
              class="relative w-full rounded-lg border border-red-400/50 bg-red-500/15 pl-3 pr-10 py-2.5 text-sm text-red-100 leading-snug"
            >
              <button
                type="button"
                class="absolute top-2 right-2 p-1 rounded-md text-red-200/90 hover:text-red-50 hover:bg-red-500/25 transition leading-none"
                aria-label="Fechar aviso"
                @click="dismissConnectorModalError"
              >
                <span class="text-lg font-light" aria-hidden="true">×</span>
              </button>
              <span class="font-semibold text-red-50 block mb-1 pr-1">Não foi possível salvar.</span>
              <span class="text-red-100/95 break-words pr-1">{{ connectorModalError }}</span>
            </div>
            <div
              v-if="connectorTestError"
              role="alert"
              class="relative w-full rounded-lg border border-red-400/50 bg-red-500/15 pl-3 pr-10 py-2.5 text-sm text-red-100 leading-snug"
            >
              <button
                type="button"
                class="absolute top-2 right-2 p-1 rounded-md text-red-200/90 hover:text-red-50 hover:bg-red-500/25 transition leading-none"
                aria-label="Fechar aviso"
                @click="dismissConnectorTestError"
              >
                <span class="text-lg font-light" aria-hidden="true">×</span>
              </button>
              <span class="font-semibold text-red-50 block mb-1 pr-1">Erro no teste.</span>
              <span class="text-red-100/95 break-words pr-1">{{ connectorTestError }}</span>
            </div>
            <div class="flex justify-between items-center gap-2">
              <div class="flex gap-2 items-center">
                <button
                  type="button"
                  :disabled="connectorTestLoading || connectorModalLoading"
                  class="px-4 py-2 text-sm rounded-lg border border-cyan-400/50 text-cyan-100 hover:bg-cyan-400/10 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  @click="testConnectorConnection"
                >
                  {{ connectorTestLoading ? 'Testando…' : 'Testar conexão' }}
                </button>
              </div>
              <div class="flex gap-2 items-center">
                <button
                  type="submit"
                  :disabled="connectorSaving || connectorModalLoading"
                  class="px-4 py-2 text-sm rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {{ connectorSaving ? 'Salvando…' : 'Salvar' }}
                </button>
                <button
                  type="button"
                  class="px-4 py-2 text-sm rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800/70"
                  @click="closeConnectorModal"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- View query modal -->
    <div
      v-if="viewQueryModalOpen"
      class="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div class="px-6 py-4 bg-[#2f4563] border-b border-cyan-200/25 flex justify-between items-center flex-shrink-0">
          <h2 class="text-xl font-semibold text-slate-100">
            {{ editingViewQueryId ? 'Editar view' : 'Nova view' }}
          </h2>
          <button @click="closeViewQueryModal" class="text-slate-500 hover:text-cyan-300 transition">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form @submit.prevent="openViewQuerySaveConfirm" class="flex flex-col flex-1 min-h-0">
          <div
            class="px-6 py-4 overflow-y-auto flex-1 min-h-0 [scrollbar-width:thin] [scrollbar-color:rgba(103,232,249,0.45)_rgba(47,69,99,0.4)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#2f4563]/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-cyan-300/45 [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-[#2f4563]/80 [&::-webkit-scrollbar-thumb:hover]:bg-cyan-300/70"
          >
            <div v-if="viewQueryModalLoading" class="py-16 text-center text-slate-400 text-sm">
              Carregando dados da view…
            </div>
            <template v-else>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-slate-300 mb-1">Nome amigável <span class="text-red-400">*</span></label>
                <input
                  v-model="viewQueryForm.name"
                  type="text"
                  required
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                />
              </div>

              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-slate-300 mb-1">Chave da operação <span class="text-red-400">*</span></label>
                <input
                  v-model="viewQueryForm.operation_key"
                  type="text"
                  required
                  placeholder="Ex.: Listar manifests ou listar_manifests"
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70 font-mono text-sm"
                />
                <p class="text-xs text-slate-300 mt-1 leading-snug">
                  Identificador técnico da operação. Ajuda em logs, rastreabilidade e integrações com outros sistemas.
                </p>
              </div>

              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-slate-300 mb-1">Nome da View <span class="text-red-400">*</span></label>
                <input
                  v-model="viewQueryForm.view_name"
                  type="text"
                  required
                  placeholder="ex.: analytics.manifests_v"
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                />
              </div>

              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-slate-300 mb-1">Descrição da View</label>
                <textarea
                  v-model="viewQueryForm.description"
                  rows="2"
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                />
              </div>

              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-slate-300 mb-1">Instruções para o modelo (LLM)</label>
                <textarea
                  v-model="viewQueryForm.llm_instructions"
                  rows="4"
                  placeholder="Regras de filtros, períodos, colunas, etc. Incluídas no chat junto ao prompt do agente."
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                />
                <p class="text-xs text-slate-400 mt-1 leading-snug">
                  Texto opcional só para o modelo. Máx. 12&nbsp;000 caracteres.
                </p>
              </div>

              <div class="md:col-span-2">
                <div class="flex items-center justify-between mb-2 gap-3">
                  <h3 class="text-sm font-semibold text-slate-200">Parâmetros (mínimos esperados)</h3>
                  <button type="button" @click="addParamRow" class="text-cyan-300 hover:text-cyan-200 text-sm">
                    + Parâmetro
                  </button>
                </div>

                <div class="space-y-3">
                  <div
                    v-for="(row, idx) in viewQueryForm.parameter_schema"
                    :key="idx"
                    class="space-y-2 rounded-lg border border-slate-600/50 bg-slate-950/30 p-2.5"
                  >
                    <div class="grid grid-cols-1 md:grid-cols-12 gap-2 md:items-center">
                      <input
                        v-model="row.column"
                        type="text"
                        placeholder="Nome do campo"
                        class="md:col-span-4 min-w-0 w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-950/40 text-slate-100 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
                      />
                      <select
                        v-model="row.operator"
                        class="md:col-span-1 min-w-0 w-full px-2 py-2 rounded-lg border border-slate-600 bg-slate-950/40 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
                      >
                        <option value="=">=</option>
                        <option value=">=">&gt;=</option>
                        <option value="<=">&lt;=</option>
                        <option value="IN">IN</option>
                        <option value="LIKE">LIKE</option>
                        <option value="BETWEEN">BETWEEN</option>
                      </select>
                      <select
                        v-model="row.type"
                        class="md:col-span-2 min-w-0 w-full px-2 py-2 rounded-lg border border-slate-600 bg-slate-950/40 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
                      >
                        <option value="string">string</option>
                        <option value="integer">integer</option>
                        <option value="number">number</option>
                        <option value="boolean">boolean</option>
                        <option value="date">date</option>
                        <option value="datetime">datetime</option>
                        <option value="date_range">date_range</option>
                      </select>
                      <input
                        v-model="row.test_value"
                        type="text"
                        placeholder="Valor para teste"
                        class="md:col-span-5 min-w-0 w-full px-2 py-2 rounded-lg border border-slate-600 bg-slate-950/40 text-slate-100 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
                      />
                    </div>
                    <div class="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-700/40 pt-2">
                      <label
                        class="inline-flex items-center gap-1.5 text-xs text-slate-300 whitespace-nowrap shrink-0"
                        title="Sempre usa o valor de teste; ignora chat/LLM"
                      >
                        <input type="checkbox" v-model="row.fixed" class="rounded border-slate-500 shrink-0" />
                        Fixo
                      </label>
                      <label class="inline-flex items-center gap-1.5 text-xs text-slate-300 whitespace-nowrap shrink-0">
                        <input type="checkbox" v-model="row.required" class="rounded border-slate-500 shrink-0" />
                        Obrigatório
                      </label>
                      <button
                        type="button"
                        class="ml-auto inline-flex shrink-0 items-center justify-center rounded px-2 py-0.5 text-lg leading-none text-red-300 transition hover:bg-red-500/10 hover:text-red-200"
                        title="Remover parâmetro"
                        aria-label="Remover parâmetro"
                        @click="removeParamRow(idx)"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="md:col-span-2 rounded-lg border border-slate-600/50 bg-slate-950/30 p-3 space-y-2">
                <label class="block text-sm font-medium text-slate-300">Schema dos parâmetros (formato, obrigatório, exceções)</label>
                <p class="text-xs text-slate-500 leading-relaxed">
                  Opcional. Alinhe as chaves de <span class="font-mono text-slate-400">fields</span> aos nomes de coluna dos parâmetros acima.
                  <span class="font-mono text-slate-400">required_date_exceptions</span>: paths que isentam obrigatoriedade de datas com
                  <span class="font-mono">required: true</span>. Tipos: date, datetime, string, number, integer, boolean.
                </p>
                <textarea
                  v-model="viewQueryForm.parameter_field_schema_json"
                  rows="10"
                  placeholder='{"fields":{"service_date":{"format":"date_us","required":true}},"required_date_exceptions":["cte_number"]}'
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                />
              </div>
            </div>

            <div class="mt-6 rounded-lg border border-cyan-400/25 bg-slate-950/45 p-3 space-y-3 text-xs">
              <h4 class="text-sm font-medium text-slate-200">Pré-visualização</h4>

              <div>
                <p class="text-[10px] uppercase tracking-wide text-slate-300 mb-1">Comando SQL</p>
                <pre class="min-h-[2.75rem] p-2 rounded bg-slate-900/85 text-slate-300 overflow-x-auto whitespace-pre-wrap font-mono text-[11px]">{{ viewQueryTestResult?.sql_query || '—' }}</pre>
              </div>

              <div>
                <p class="text-[10px] uppercase tracking-wide text-slate-300 mb-1">Binds para Where</p>
                <pre class="min-h-[2.75rem] p-2 rounded bg-slate-900/85 text-slate-300 overflow-x-auto whitespace-pre-wrap font-mono text-[11px]">{{ viewQueryTestResult ? JSON.stringify(viewQueryTestResult.binds, null, 2) : '—' }}</pre>
              </div>

              <div>
                <p class="text-[10px] uppercase tracking-wide text-slate-300 mb-1">
                  Resultado
                  <template v-if="viewQueryTestResult && Array.isArray(viewQueryTestResult.rows)">
                    ({{ viewQueryTestResult.rows_count ?? viewQueryTestResult.rows.length }} linha(s))
                  </template>
                  <template v-else> (—)</template>
                </p>
                <pre class="min-h-[2.75rem] max-h-64 p-2 rounded bg-slate-900/85 text-slate-300 overflow-x-auto whitespace-pre-wrap font-mono text-[11px]">{{ viewQueryRowsPreview }}</pre>
              </div>
            </div>
            </template>
          </div>

          <div class="px-6 py-4 border-t border-slate-600/70 flex flex-col gap-3 flex-shrink-0 bg-slate-700/55">
            <div
              v-if="viewQueryModalError && !viewQueryModalLoading"
              role="alert"
              class="relative w-full rounded-lg border border-red-400/50 bg-red-500/15 pl-3 pr-10 py-2.5 text-sm text-red-100 leading-snug"
            >
              <button
                type="button"
                class="absolute top-2 right-2 p-1 rounded-md text-red-200/90 hover:text-red-50 hover:bg-red-500/25 transition leading-none"
                aria-label="Fechar aviso"
                @click="dismissViewQueryModalError"
              >
                <span class="text-lg font-light" aria-hidden="true">×</span>
              </button>
              <span class="font-semibold text-red-50 block mb-1 pr-1">Não foi possível salvar.</span>
              <span class="text-red-100/95 break-words pr-1">{{ viewQueryModalError }}</span>
            </div>
            <div
              v-if="viewQueryTestError"
              role="alert"
              class="relative w-full rounded-lg border border-red-400/50 bg-red-500/15 pl-3 pr-10 py-2.5 text-sm text-red-100 leading-snug"
            >
              <button
                type="button"
                class="absolute top-2 right-2 p-1 rounded-md text-red-200/90 hover:text-red-50 hover:bg-red-500/25 transition leading-none"
                aria-label="Fechar aviso"
                @click="dismissViewQueryTestError"
              >
                <span class="text-lg font-light" aria-hidden="true">×</span>
              </button>
              <span class="font-semibold text-red-50 block mb-1 pr-1">Erro no teste.</span>
              <span class="text-red-100/95 break-words pr-1">{{ viewQueryTestError }}</span>
            </div>
            <div class="flex flex-wrap justify-between items-center gap-2">
              <button
                type="button"
                :disabled="viewQueryTestLoading || viewQueryModalLoading"
                class="px-4 py-2 text-sm rounded-lg border border-cyan-400/50 text-cyan-100 hover:bg-cyan-400/10 transition disabled:opacity-50 disabled:cursor-not-allowed"
                @click="testViewQuery"
              >
                {{ viewQueryTestLoading ? 'Testando…' : 'Testar consulta' }}
              </button>
              <div class="flex flex-wrap gap-2 justify-end">
                <button
                  type="submit"
                  :disabled="viewQuerySaving || viewQueryModalLoading"
                  class="px-4 py-2 text-sm rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {{ viewQuerySaving ? 'Salvando…' : 'Salvar' }}
                </button>
                <button
                  type="button"
                  class="px-4 py-2 text-sm rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800/70"
                  @click="closeViewQueryModal"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Save confirm connector -->
    <div
      v-if="connectorSaveConfirmOpen"
      class="fixed inset-0 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      @click.self="closeConnectorSaveConfirm"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-md w-full p-6">
        <h3 class="flex items-center text-lg font-semibold text-slate-100 mb-4">
          <svg class="w-6 h-6 text-amber-500 mr-2 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z"
              clip-rule="evenodd"
            />
          </svg>
          Confirmação!
        </h3>
        <p class="text-slate-300 mb-6">Deseja salvar este conector de banco de dados?</p>
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            :disabled="connectorSaving"
            class="px-4 py-2 bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold rounded-lg transition disabled:opacity-50"
            @click="confirmSaveConnector"
          >
            {{ connectorSaving ? 'Salvando…' : 'Sim' }}
          </button>
          <button
            type="button"
            :disabled="connectorSaving"
            class="px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800/70 transition disabled:opacity-50"
            @click="closeConnectorSaveConfirm"
          >
            Não
          </button>
        </div>
      </div>
    </div>

    <!-- Save confirm view query -->
    <div
      v-if="viewQuerySaveConfirmOpen"
      class="fixed inset-0 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      @click.self="closeViewQuerySaveConfirm"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-md w-full p-6">
        <h3 class="flex items-center text-lg font-semibold text-slate-100 mb-4">
          <svg class="w-6 h-6 text-amber-500 mr-2 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z"
              clip-rule="evenodd"
            />
          </svg>
          Confirmação!
        </h3>
        <p class="text-slate-300 mb-6">Deseja salvar esta view parametrizada?</p>
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            :disabled="viewQuerySaving"
            class="px-4 py-2 bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold rounded-lg transition disabled:opacity-50"
            @click="confirmSaveViewQuery"
          >
            {{ viewQuerySaving ? 'Salvando…' : 'Sim' }}
          </button>
          <button
            type="button"
            :disabled="viewQuerySaving"
            class="px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800/70 transition disabled:opacity-50"
            @click="closeViewQuerySaveConfirm"
          >
            Não
          </button>
        </div>
      </div>
    </div>

    <!-- Delete confirm connector -->
    <div
      v-if="connectorDeleteConfirm"
      class="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-md w-full p-6">
        <h3 class="text-lg font-semibold text-slate-100 mb-2">Excluir conector?</h3>
        <p class="text-slate-300 mb-4">
          Tem certeza que deseja excluir <strong class="text-slate-100">{{ connectorDeleteConfirm.name }}</strong>?
        </p>
        <div class="flex justify-end gap-2">
          <button
            type="button"
            class="px-4 py-2 rounded-lg border border-slate-500/80 text-slate-200 hover:bg-slate-800/60 transition font-medium"
            @click="connectorDeleteConfirm = null"
          >
            Cancelar
          </button>
          <button
            type="button"
            :disabled="connectorDeleting"
            class="px-4 py-2 rounded-lg bg-red-500/20 border border-red-400/40 text-red-200 hover:bg-red-500/25 transition disabled:opacity-50 disabled:cursor-not-allowed"
            @click="executeConnectorDelete"
          >
            {{ connectorDeleting ? 'Excluindo…' : 'Excluir' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete confirm view query -->
    <div
      v-if="viewQueryDeleteConfirm"
      class="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-md w-full p-6">
        <h3 class="text-lg font-semibold text-slate-100 mb-2">Excluir view parametrizada?</h3>
        <p class="text-slate-300 mb-4">
          Tem certeza que deseja excluir <strong class="text-slate-100">{{ viewQueryDeleteConfirm.q.name }}</strong>?
        </p>
        <div class="flex justify-end gap-2">
          <button
            type="button"
            class="px-4 py-2 rounded-lg border border-slate-500/80 text-slate-200 hover:bg-slate-800/60 transition font-medium"
            @click="viewQueryDeleteConfirm = null"
          >
            Cancelar
          </button>
          <button
            type="button"
            :disabled="viewQueryDeleting"
            class="px-4 py-2 rounded-lg bg-red-500/20 border border-red-400/40 text-red-200 hover:bg-red-500/25 transition disabled:opacity-50 disabled:cursor-not-allowed"
            @click="executeViewQueryDelete"
          >
            {{ viewQueryDeleting ? 'Excluindo…' : 'Excluir' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useApi } from '../composables/useApi.js'

function formatParameterFieldSchemaJsonForForm(raw) {
  const o = raw && typeof raw === 'object' ? raw : {}
  const fields = o.fields && typeof o.fields === 'object' ? o.fields : {}
  const exc = Array.isArray(o.required_date_exceptions) ? o.required_date_exceptions : []
  return JSON.stringify({ fields, required_date_exceptions: exc }, null, 2)
}

function defaultParameterFieldSchemaJson() {
  return JSON.stringify({ fields: {}, required_date_exceptions: [] }, null, 2)
}

export default {
  name: 'DatabaseConnectors',

  data() {
    return {
      loading: false,
      loadError: '',

      connectors: [],
      expandedId: null,
      detailLoading: false,
      detail: null,

      // Connector modal state
      connectorModalOpen: false,
      connectorModalLoading: false,
      connectorSaving: false,
      connectorModalError: '',
      editingConnectorId: null,
      showConnectorPassword: false,
      connectorForm: {
        name: '',
        db_type: 'postgresql',
        host: '',
        port: '',
        database: '',
        username: '',
        password: '',
        clear_password: false,
        active: true
      },

      connectorDeleteConfirm: null,
      connectorDeleting: false,
      connectorSaveConfirmOpen: false,

      // Connector test state
      connectorTestLoading: false,
      connectorTestError: '',
      connectorTestResult: null,

      // View query modal state
      viewQueryModalOpen: false,
      viewQueryModalLoading: false,
      viewQuerySaving: false,
      viewQueryModalError: '',
      editingViewQueryId: null,
      endpointParentConnectorId: null,

      viewQueryTestLoading: false,
      viewQueryTestError: '',
      viewQueryTestResult: null,
      viewQueryDeleteConfirm: null,
      viewQueryDeleting: false,
      viewQuerySaveConfirmOpen: false,
      viewQueryForm: {
        name: '',
        operation_key: '',
        view_name: '',
        description: '',
        llm_instructions: '',
        active: true,
        parameter_schema: [],
        parameter_field_schema_json: defaultParameterFieldSchemaJson()
      }
    }
  },

  computed: {
    viewQueryRowsPreview() {
      const r = this.viewQueryTestResult
      if (!r || !Array.isArray(r.rows)) return '—'
      return JSON.stringify(r.rows, null, 2)
    }
  },

  methods: {
    openConnectorSaveConfirm() {
      if (this.connectorModalLoading || this.connectorSaving) return
      this.connectorSaveConfirmOpen = true
    },
    closeConnectorSaveConfirm() {
      if (this.connectorSaving) return
      this.connectorSaveConfirmOpen = false
    },
    async confirmSaveConnector() {
      if (this.connectorSaving) return
      await this.saveConnector()
    },
    openViewQuerySaveConfirm() {
      if (this.viewQueryModalLoading || this.viewQuerySaving) return
      this.viewQuerySaveConfirmOpen = true
    },
    closeViewQuerySaveConfirm() {
      if (this.viewQuerySaving) return
      this.viewQuerySaveConfirmOpen = false
    },
    async confirmSaveViewQuery() {
      if (this.viewQuerySaving) return
      await this.saveViewQuery()
    },

    dismissConnectorModalError() {
      this.connectorModalError = ''
    },
    dismissConnectorTestError() {
      this.connectorTestError = ''
    },
    dismissViewQueryModalError() {
      this.viewQueryModalError = ''
    },
    dismissViewQueryTestError() {
      this.viewQueryTestError = ''
    },

    emptyConnectorForm() {
      return {
        name: '',
        db_type: 'postgresql',
        host: '',
        port: '',
        database: '',
        username: '',
        password: '',
        clear_password: false,
        active: true
      }
    },

    emptyViewQueryForm() {
      return {
        name: '',
        operation_key: '',
        view_name: '',
        description: '',
        llm_instructions: '',
        active: true,
        parameter_schema: [this.emptyParamRow()],
        parameter_field_schema_json: defaultParameterFieldSchemaJson()
      }
    },

    applyViewQueryFromShow(q) {
      this.viewQueryForm = {
        name: q?.name || '',
        operation_key: q?.operation_key || '',
        view_name: q?.view_name || '',
        description: q?.description || '',
        llm_instructions: q?.llm_instructions || '',
        active: q?.active !== false,
        parameter_schema: (q?.parameter_schema || []).length
          ? q.parameter_schema.map((p) => ({
              name: p.name || '',
              column: p.column || '',
              operator: p.operator || '=',
              type: p.type || 'string',
              required: !!p.required,
              fixed: !!p.fixed,
              test_value:
                p.test_value != null && p.test_value !== undefined ? String(p.test_value) : ''
            }))
          : [this.emptyParamRow()],
        parameter_field_schema_json: formatParameterFieldSchemaJsonForForm(q?.parameter_field_schema)
      }
    },

    emptyParamRow() {
      return {
        column: '',
        operator: '=',
        type: 'string',
        required: false,
        fixed: false,
        test_value: ''
      }
    },

    async loadConnectors() {
      const { get } = useApi()
      this.loading = true
      this.loadError = ''
      const res = await get('/api/v1/database_connectors')
      this.loading = false
      if (res.error) {
        this.loadError = res.error.response?.data?.errors?.join?.(', ') || 'Erro ao carregar conectores.'
        return
      }
      this.connectors = res.data?.database_connectors || []
    },

    toggleExpand(id) {
      if (this.expandedId === id) {
        this.expandedId = null
        this.detail = null
        return
      }
      this.expandedId = id
      this.detailLoading = true
      this.detail = null
      this.loadConnectorDetail(id)
    },

    async loadConnectorDetail(id) {
      const { get } = useApi()
      const res = await get(`/api/v1/database_connectors/${id}`)
      this.detailLoading = false
      if (res.error || !res.data?.database_connector) {
        this.loadError = 'Erro ao carregar detalhes do conector.'
        return
      }
      this.detail = res.data.database_connector
    },

    async openConnectorModal(connectorId) {
      this.connectorModalError = ''
      this.editingConnectorId = connectorId

      if (!connectorId) {
        this.connectorModalLoading = false
        this.connectorForm = this.emptyConnectorForm()
        this.showConnectorPassword = false
        this.connectorModalOpen = true
        return
      }

      this.connectorForm = this.emptyConnectorForm()
      this.showConnectorPassword = false
      this.connectorModalLoading = true
      this.connectorModalOpen = true

      const { get } = useApi()
      const res = await get(`/api/v1/database_connectors/${connectorId}`)
      this.connectorModalLoading = false

      if (!this.connectorModalOpen || this.editingConnectorId !== connectorId) return

      if (res.error || !res.data?.database_connector) {
        const msg = res.error?.response?.data?.errors
        this.connectorModalError = Array.isArray(msg)
          ? msg.join(', ')
          : res.error?.response?.data?.error || 'Não foi possível carregar o conector. Tente novamente.'
        return
      }

      const c = res.data.database_connector
      this.connectorForm = {
        name: c.name || '',
        db_type: c.db_type || 'postgresql',
        host: c.host || '',
        port: c.port ?? '',
        database: c.database || '',
        username: c.username || '',
        password: '',
        clear_password: false,
        active: c.active !== false
      }

      if (this.expandedId === connectorId) {
        this.detail = c
      }
    },

    closeConnectorModal() {
      this.connectorModalOpen = false
      this.connectorModalLoading = false
      this.connectorSaving = false
      this.connectorModalError = ''
      this.editingConnectorId = null
      this.connectorTestLoading = false
      this.connectorTestError = ''
      this.connectorTestResult = null
      this.connectorSaveConfirmOpen = false
    },

    async testConnectorConnection() {
      if (this.connectorModalLoading) return
      this.connectorTestLoading = true
      this.connectorTestError = ''
      this.connectorTestResult = null

      try {
        const { post } = useApi()
        const payload = {
          database_connector: {
            id: this.editingConnectorId,
            db_type: this.connectorForm.db_type,
            host: this.connectorForm.host,
            port: this.connectorForm.port === '' ? null : this.connectorForm.port,
            database: this.connectorForm.database?.trim() || '',
            username: this.connectorForm.username?.trim() || '',
            clear_password: this.connectorForm.clear_password,
            password: this.connectorForm.clear_password ? null : this.connectorForm.password
          }
        }

        const res = await post('/api/v1/database_connectors/test_connection', payload)
        if (res.error) {
          const d = res.error.response?.data
          this.connectorTestError = d?.error || (Array.isArray(d?.errors) ? d.errors.join(', ') : null) || 'Erro ao testar conexão.'
          return
        }
        this.connectorTestResult = res.data
      } finally {
        this.connectorTestLoading = false
      }
    },

    async saveConnector() {
      if (this.connectorModalLoading) return
      this.connectorSaving = true
      this.connectorModalError = ''
      const { post, put } = useApi()

      const payload = {
        database_connector: {
          name: this.connectorForm.name.trim(),
          db_type: this.connectorForm.db_type,
          host: this.connectorForm.host.trim(),
          port: this.connectorForm.port === '' ? null : this.connectorForm.port,
          database: this.connectorForm.database?.trim() || '',
          username: this.connectorForm.username?.trim() || '',
          active: this.connectorForm.active
        }
      }

      if (this.connectorForm.clear_password) {
        payload.database_connector.clear_password = true
      } else if (this.connectorForm.password?.trim()) {
        payload.database_connector.password = this.connectorForm.password
      }

      let res
      if (this.editingConnectorId) {
        res = await put(`/api/v1/database_connectors/${this.editingConnectorId}`, payload)
      } else {
        res = await post('/api/v1/database_connectors', payload)
      }

      this.connectorSaving = false
      if (res.error) {
        const err = res.error.response?.data?.errors
        this.connectorModalError = Array.isArray(err) ? err.join(', ') : err || 'Erro ao salvar conector.'
        return
      }

      this.closeConnectorModal()
      await this.loadConnectors()
      if (this.expandedId) await this.loadConnectorDetail(this.expandedId)
    },

    openConnectorDeleteConfirm(connector) {
      this.connectorDeleteConfirm = connector
    },

    async executeConnectorDelete() {
      if (!this.connectorDeleteConfirm) return
      this.connectorDeleting = true
      const { delete: del } = useApi()
      const id = this.connectorDeleteConfirm.id
      const res = await del(`/api/v1/database_connectors/${id}`)
      this.connectorDeleting = false
      if (res.error) return

      if (this.expandedId === id) {
        this.expandedId = null
        this.detail = null
      }
      this.connectorDeleteConfirm = null
      this.connectors = this.connectors.filter((c) => String(c.id) !== String(id))
    },

    async openViewQueryModal(connectorId, viewQueryId) {
      this.endpointParentConnectorId = connectorId
      this.viewQueryModalError = ''
      this.viewQueryTestError = ''
      this.viewQueryTestResult = null
      this.editingViewQueryId = viewQueryId

      if (!viewQueryId) {
        this.viewQueryModalLoading = false
        this.viewQueryForm = this.emptyViewQueryForm()
        this.viewQueryModalOpen = true
        return
      }

      this.viewQueryForm = this.emptyViewQueryForm()
      this.viewQueryModalLoading = true
      this.viewQueryModalOpen = true

      const { get } = useApi()
      const res = await get(`/api/v1/database_connectors/${connectorId}/connector_views/${viewQueryId}`)
      this.viewQueryModalLoading = false

      if (
        !this.viewQueryModalOpen ||
        this.editingViewQueryId !== viewQueryId ||
        this.endpointParentConnectorId !== connectorId
      ) {
        return
      }

      if (res.error || !res.data?.connector_view) {
        const msg = res.error?.response?.data?.errors
        this.viewQueryModalError = Array.isArray(msg)
          ? msg.join(', ')
          : res.error?.response?.data?.error || 'Não foi possível carregar a view. Tente novamente.'
        return
      }

      const q = res.data.connector_view
      this.applyViewQueryFromShow(q)

      if (this.expandedId === connectorId && this.detail?.views) {
        const i = this.detail.views.findIndex((x) => x.id === viewQueryId)
        if (i >= 0) this.detail.views.splice(i, 1, q)
      }
    },

    closeViewQueryModal() {
      this.viewQueryModalOpen = false
      this.viewQueryModalLoading = false
      this.viewQuerySaving = false
      this.viewQueryModalError = ''
      this.editingViewQueryId = null
      this.endpointParentConnectorId = null
      this.viewQueryTestLoading = false
      this.viewQueryTestError = ''
      this.viewQueryTestResult = null
      this.viewQueryForm = this.emptyViewQueryForm()
      this.viewQuerySaveConfirmOpen = false
    },

    addParamRow() {
      this.viewQueryForm.parameter_schema.push(this.emptyParamRow())
    },

    removeParamRow(idx) {
      this.viewQueryForm.parameter_schema.splice(idx, 1)
      if (this.viewQueryForm.parameter_schema.length === 0) {
        this.viewQueryForm.parameter_schema.push(this.emptyParamRow())
      }
    },

    async testViewQuery() {
      if (this.viewQueryModalLoading) return
      this.viewQueryTestLoading = true
      this.viewQueryTestError = ''

      try {
        const { post } = useApi()
        const connectorId = this.endpointParentConnectorId
        if (!connectorId) throw new Error('Conector em falta.')
        if (!this.viewQueryForm.view_name?.trim()) throw new Error('Indique o nome da view.')

        const schema = (this.viewQueryForm.parameter_schema || [])
          .filter((r) => r.column && String(r.column).trim())
          .map((r) => {
            const row = {
              column: r.column?.trim(),
              operator: r.operator || '=',
              type: r.type || 'string',
              required: !!r.required
            }
            if (r.fixed) row.fixed = true
            const tv = String(r.test_value ?? '').trim()
            if (tv) row.test_value = tv
            return row
          })

        const testValues = {}
        for (const r of this.viewQueryForm.parameter_schema || []) {
          const c = r.column?.trim()
          if (!c) continue
          testValues[c] = r.test_value ?? ''
        }

        let parameterFieldSchema = {}
        const rawPfs = (this.viewQueryForm.parameter_field_schema_json || '').trim()
        if (rawPfs) {
          try {
            const parsed = JSON.parse(rawPfs)
            if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
              throw new Error('Schema dos parâmetros deve ser um objecto JSON.')
            }
            parameterFieldSchema = parsed
          } catch (e) {
            this.viewQueryTestError =
              e instanceof SyntaxError
                ? 'Schema dos parâmetros JSON inválido (sintaxe).'
                : e?.message || 'Schema dos parâmetros JSON inválido.'
            return
          }
        }

        const payload = {
          test: {
            view_name: this.viewQueryForm.view_name.trim(),
            parameter_schema: schema,
            parameter_field_schema: parameterFieldSchema,
            test_values: testValues,
            limit: 50
          }
        }

        const res = await post(`/api/v1/database_connectors/${connectorId}/connector_views/test_execute`, payload)
        if (res.error) {
          const d = res.error.response?.data
          this.viewQueryTestError = d?.error || d?.errors?.join?.(', ') || 'Erro ao testar consulta.'
          return
        }
        this.viewQueryTestResult = res.data
      } finally {
        this.viewQueryTestLoading = false
      }
    },

    async saveViewQuery() {
      if (this.viewQueryModalLoading) return
      this.viewQuerySaving = true
      this.viewQueryModalError = ''
      const { post, put } = useApi()

      const connectorId = this.endpointParentConnectorId
      if (!connectorId) {
        this.viewQuerySaving = false
        this.viewQueryModalError = 'Conector em falta.'
        return
      }

      const schema = (this.viewQueryForm.parameter_schema || [])
        .filter((r) => r.column && String(r.column).trim())
        .map((r) => {
          const row = {
            column: r.column.trim(),
            operator: r.operator || '=',
            type: r.type || 'string',
            required: !!r.required
          }
          if (r.fixed) row.fixed = true
          const tv = String(r.test_value ?? '').trim()
          if (tv) row.test_value = tv
          return row
        })

      let parameterFieldSchema = {}
      const rawPfs = (this.viewQueryForm.parameter_field_schema_json || '').trim()
      if (rawPfs) {
        try {
          const parsed = JSON.parse(rawPfs)
          if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
            throw new Error('Schema dos parâmetros deve ser um objecto JSON.')
          }
          parameterFieldSchema = parsed
        } catch (e) {
          this.viewQuerySaving = false
          this.viewQueryModalError =
            e instanceof SyntaxError
              ? 'Schema dos parâmetros JSON inválido (sintaxe).'
              : e?.message || 'Schema dos parâmetros JSON inválido.'
          return
        }
      }

      const payload = {
        connector_view: {
          name: this.viewQueryForm.name.trim(),
          operation_key: this.viewQueryForm.operation_key.trim(),
          view_name: this.viewQueryForm.view_name.trim(),
          description: this.viewQueryForm.description?.trim() || null,
          llm_instructions: this.viewQueryForm.llm_instructions?.trim() || null,
          active: this.viewQueryForm.active,
          parameter_schema: schema,
          parameter_field_schema: parameterFieldSchema
        }
      }

      let res
      if (this.editingViewQueryId) {
        res = await put(
          `/api/v1/database_connectors/${connectorId}/connector_views/${this.editingViewQueryId}`,
          payload
        )
      } else {
        res = await post(`/api/v1/database_connectors/${connectorId}/connector_views`, payload)
      }

      this.viewQuerySaving = false
      if (res.error) {
        const err = res.error.response?.data?.errors
        this.viewQueryModalError = Array.isArray(err) ? err.join(', ') : err || 'Erro ao salvar view.'
        return
      }

      this.closeViewQueryModal()
      await this.loadConnectors()
      await this.loadConnectorDetail(connectorId)
    },

    openViewQueryDeleteConfirm(connectorId, q) {
      this.viewQueryDeleteConfirm = { connectorId, q }
    },

    async executeViewQueryDelete() {
      if (!this.viewQueryDeleteConfirm) return
      this.viewQueryDeleting = true
      const { delete: del } = useApi()
      const { connectorId, q } = this.viewQueryDeleteConfirm
      const res = await del(`/api/v1/database_connectors/${connectorId}/connector_views/${q.id}`)
      this.viewQueryDeleting = false
      if (res.error) return

      this.viewQueryDeleteConfirm = null
      const row = this.connectors.find((c) => String(c.id) === String(connectorId))
      if (row && typeof row.views_count === 'number' && row.views_count > 0) {
        row.views_count -= 1
      }
      if (this.expandedId === connectorId && this.detail?.views?.length) {
        this.detail.views = this.detail.views.filter((v) => String(v.id) !== String(q.id))
      }
    }
  },

  mounted() {
    this.loadConnectors()
  }
}
</script>

